// Validate required env vars before anything else renders.
// Must be imported first so a missing var fails at init, not at runtime.
import './config/env';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { HeroUIProvider } from '@heroui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import store from './core/data/redux/store';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n.ts';
import { AuthProviderNew } from './context/AuthContext.tsx';
import ToastProviders from './utils/ToastContainer.tsx';
import { HelmetProvider } from 'react-helmet-async';
import { initTelemetry } from './lib/telemetry';
import { FlagProvider, type IConfig } from '@unleash/proxy-client-react';
import { appConfig } from './config/appConfig';

const queryClient = new QueryClient();

// Client-side flag evaluation against the Unleash Frontend API — same shape as
// gigmosaic-provider-dashboard/src/main.tsx.
const unleashConfig: IConfig = {
  url: appConfig.unleash.url,
  clientKey: appConfig.unleash.clientKey,
  appName: appConfig.unleash.appName,
  refreshInterval: appConfig.unleash.refreshInterval,
};

// Initialise telemetry BEFORE React mounts so the SDK reads the persisted
// consent cookie (`hp_consent`) before the first event flush.
initTelemetry();

const appTree = (
  <HeroUIProvider>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <ToastProviders>
            {/* <AppToastContainer /> */}
            <div className="lg:max-w-[1980px] mx-auto items-center ">
              {/* <AuthProvider {...cognitoAuthConfig}> */}
              <HelmetProvider>
                <AuthProviderNew>
                  <App />
                </AuthProviderNew>
              </HelmetProvider>
              {/* </AuthProvider> */}
            </div>
          </ToastProviders>
        </I18nextProvider>
      </QueryClientProvider>
    </Provider>
  </HeroUIProvider>
);

// Only mount FlagProvider when the Unleash env vars are actually present.
// The Unleash client throws ('url is required' / 'clientKey is required') when
// constructed without them, and Vite bakes a missing Amplify var in as
// `undefined` — so mounting it unconditionally would white-screen the entire
// site the moment a var is absent. Without the provider every flag reads as ON
// (see ./config/featureFlags.ts), so the portal degrades to "all features on"
// rather than breaking.
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {appConfig.unleash.isConfigured ? (
      <FlagProvider config={unleashConfig}>{appTree}</FlagProvider>
    ) : (
      appTree
    )}
  </StrictMode>
);
